/**
 * Admin Integrations Page
 * Setup and manage Confluence integration (Google Chat OAuth moved to Newsletter page)
 */
import { useQuery } from '@tanstack/react-query';
import { Box, Grid, Paper, Typography, Chip } from '@mui/material';
import {
  CheckCircleOutlined as ConnectedIcon,
  ErrorOutline as ErrorIcon,
  HelpOutline as NotConfiguredIcon,
} from '@mui/icons-material';
import { ConfluenceSetup } from '@/components/integrations';
import { integrationsApi } from '@/services/api';

function StatusChip({ label, configured, valid }: { label: string; configured: boolean; valid: boolean | null }) {
  if (!configured) {
    return (
      <Chip
        icon={<NotConfiguredIcon />}
        label={`${label}: Not configured`}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    );
  }
  if (valid) {
    return (
      <Chip
        icon={<ConnectedIcon />}
        label={`${label}: Connected`}
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    );
  }
  return (
    <Chip
      icon={<ErrorIcon />}
      label={`${label}: Action required`}
      size="small"
      color="warning"
      variant="outlined"
      sx={{ fontWeight: 500 }}
    />
  );
}

export default function AdminIntegrationsPage() {
  const { data: confluenceStatus } = useQuery({
    queryKey: ['confluence-auth-status'],
    queryFn: () => integrationsApi.confluenceAuthStatus().then((r) => r.data),
    refetchInterval: 30_000,
  });

  return (
    <Box>
      {/* Status overview */}
      <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mr: 1 }}>
          Status
        </Typography>
        <StatusChip
          label="Confluence"
          configured={confluenceStatus?.configured ?? false}
          valid={confluenceStatus?.valid ?? null}
        />
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ConfluenceSetup />
        </Grid>
      </Grid>
    </Box>
  );
}
